/**
 * ThemeContext - Backward-Compatible Facade
 *
 * This file now serves as a facade/adapter for the Zustand theme store.
 * All consumers continue to work without changes while using Zustand under the hood.
 *
 * MIGRATION STATUS: Phase 2 - Facade layer (backward compatible)
 * - All exports use Zustand themeStore
 * - ThemeProvider is a thin wrapper (will be removed in future phase)
 * - 39 consumers continue working without changes
 */

import React, { createContext, useContext, useMemo } from 'react'; // useContext/createContext kept for ThemeProvider backward compat
import {
  useThemeStore,
  useThemeName,
  useIsDarkMode,
  useThemeActions,
  useAllThemes,
  useThemeHelpers
} from '../store/theme/themeStore';
import { lightModeColors, darkModeColors, themes, generatePaletteFromHex } from '../store/theme/themeConstants';

// Create context for backward compatibility
// In a future phase, this will be removed entirely
const ThemeContext = createContext();

/**
 * ThemeProvider - Backward-compatible wrapper
 *
 * This is now a thin wrapper that provides the same API as before,
 * but powered by Zustand themeStore instead of React Context state.
 *
 * DOM effects (CSS custom properties) are handled automatically by
 * the themeStore via subscription in store/theme/themeStore.js
 */
export const ThemeProvider = ({ children }) => {
  // Subscribe to Zustand store (these are granular - minimal re-renders)
  const themeName = useThemeName();
  const isDarkMode = useIsDarkMode();
  const { setTheme, toggleDarkMode, setDarkMode } = useThemeActions();
  const allThemes = useAllThemes();
  const { getThemeHex, getThemeFromHex } = useThemeHelpers();

  // Create enhanced theme object (same format as before)
  const theme = useMemo(() => {
    const currentTheme = themeName?.startsWith('custom:')
      ? generatePaletteFromHex(themeName.slice(7))
      : (themes[themeName] || themes.default);
    return {
      ...currentTheme,
      colors: {
        light: lightModeColors,
        dark: darkModeColors
      }
    };
  }, [themeName]);

  // Provide the same API as before - consumers see no difference
  const value = useMemo(() => ({
    theme,
    themeName,
    isDarkMode,
    setTheme,
    toggleDarkMode,
    setDarkMode,
    getThemeHex,
    getThemeFromHex,
    allThemes
  }), [theme, themeName, isDarkMode, setTheme, toggleDarkMode, setDarkMode, getThemeHex, getThemeFromHex, allThemes]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme - Backward-compatible hook
 *
 * Returns the same API as before, but powered by Zustand.
 * No ThemeProvider wrapper required — reads directly from the global store.
 * Consumers can continue using this without any changes.
 */
export const useTheme = () => {
  const themeName = useThemeName();
  const isDarkMode = useIsDarkMode();
  const { setTheme, toggleDarkMode, setDarkMode } = useThemeActions();
  const allThemes = useAllThemes();
  const { getThemeHex, getThemeFromHex } = useThemeHelpers();

  const theme = useMemo(() => {
    const currentTheme = themeName?.startsWith('custom:')
      ? generatePaletteFromHex(themeName.slice(7))
      : (themes[themeName] || themes.default);
    return {
      ...currentTheme,
      colors: {
        light: lightModeColors,
        dark: darkModeColors
      }
    };
  }, [themeName]);

  return {
    theme,
    themeName,
    isDarkMode,
    setTheme,
    toggleDarkMode,
    setDarkMode,
    getThemeHex,
    getThemeFromHex,
    allThemes
  };
};

/**
 * Direct Zustand exports for new code
 *
 * New code can import these directly for better performance
 * (no Context re-render issues, granular subscriptions)
 */
export {
  useThemeName,
  useIsDarkMode,
  useThemeActions,
  useAllThemes,
  useThemeHelpers
} from '../store/theme/themeStore';

export { lightModeColors, darkModeColors, themes } from '../store/theme/themeConstants';
